import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Snackbar from '@mui/material/Snackbar';
import Typography from '@mui/material/Typography';
import React, { useEffect, useState } from 'react';

import { endViewAsFromTab } from './viewAsAPI';
import {
  getViewAsMsRemaining,
  getViewAsSession,
  markViewAsEnded,
  VIEW_AS_READ_ONLY_EVENT,
} from './viewAsSession';

export const VIEW_AS_BANNER_HEIGHT = 48;

/** "14:32" for the time left; never negative. */
export const formatRemaining = (ms: number): string => {
  const totalSeconds = Math.max(0, Math.ceil(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

/** Who the tab is viewing as, from the session's claims: name (or email), role, practice. */
export const describeViewedMember = (claims: Record<string, string>): string => {
  const name =
    [claims.given_name, claims.family_name].filter(Boolean).join(' ') || claims.email || 'a member';
  // The claim can carry an "#owner-email" suffix; only the practice name is shown.
  const practice = (claims['custom:practice_name'] ?? '').split('#')[0].trim();
  const role = claims['custom:role'] === 'PracticeAdmin' ? 'Practice admin' : 'Practitioner';

  return [name, role, practice].filter(Boolean).join(', ');
};

/**
 * HA-447: the fixed, non-dismissable banner across the bottom of a support view-as tab. It names
 * who the tab is viewing as, says it is read-only, counts down to the session's expiry (ending it
 * there) and has the End button. A write the API refuses shows a quiet notice here instead of
 * the app's "Access denied" toast.
 */
const ViewAsBanner: React.FC = () => {
  const session = getViewAsSession();
  const [remaining, setRemaining] = useState<number>(() => getViewAsMsRemaining() ?? 0);
  const [ending, setEnding] = useState(false);
  const [readOnlyNotice, setReadOnlyNotice] = useState(false);

  useEffect(() => {
    const tick = () => {
      const ms = getViewAsMsRemaining() ?? 0;

      setRemaining(ms);
      if (ms <= 0) {
        markViewAsEnded();
      }
    };
    const id = window.setInterval(tick, 1000);

    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    const onReadOnly = () => setReadOnlyNotice(true);

    window.addEventListener(VIEW_AS_READ_ONLY_EVENT, onReadOnly);

    return () => window.removeEventListener(VIEW_AS_READ_ONLY_EVENT, onReadOnly);
  }, []);

  // Keep the page's own content clear of the banner.
  useEffect(() => {
    const previous = document.body.style.paddingBottom;

    document.body.style.paddingBottom = `${VIEW_AS_BANNER_HEIGHT}px`;

    return () => {
      document.body.style.paddingBottom = previous;
    };
  }, []);

  const end = () => {
    setEnding(true);
    void endViewAsFromTab();
  };

  return (
    <>
      <Box
        role="region"
        aria-label="Support session"
        data-testid="view-as-banner"
        sx={{
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 0,
          minHeight: VIEW_AS_BANNER_HEIGHT,
          zIndex: (theme) => theme.zIndex.snackbar + 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexWrap: 'wrap',
          gap: 2,
          px: 2,
          py: 0.5,
          bgcolor: 'warning.dark',
          color: 'warning.contrastText',
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          Viewing as {session ? describeViewedMember(session.claims) : 'a practice member'}
        </Typography>
        <Typography variant="body2">Read-only</Typography>
        <Typography variant="body2" aria-live="off" data-testid="view-as-remaining">
          Ends in {formatRemaining(remaining)}
        </Typography>
        <Button
          size="small"
          variant="contained"
          color="inherit"
          onClick={end}
          disabled={ending}
          sx={{ color: 'warning.dark' }}
        >
          End session
        </Button>
      </Box>
      <Snackbar
        open={readOnlyNotice}
        autoHideDuration={4000}
        onClose={() => setReadOnlyNotice(false)}
        message="Read-only in a support session"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ bottom: { xs: VIEW_AS_BANNER_HEIGHT + 8, sm: VIEW_AS_BANNER_HEIGHT + 8 } }}
      />
    </>
  );
};

export default ViewAsBanner;
